import React, { useState } from "react";
import axiosLamApi from "../../library/axios-lam-api";
import {
  IconInfoCircle,
  IconX,
  IconTag,
  IconCalendar,
  IconExternalLink,
  IconRefresh,
} from "@tabler/icons-react";
import { APP_VERSION } from "../../constants/appVersion";
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';

const PRIMARY = "#26196b";

const formatReleasedAt = (value) => {
  if (!value) return "-";
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// deskripsi dari API berupa HTML dari editor, buang tag yang berpotensi berbahaya
const sanitizeHtml = (html) =>
  String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/ on\w+="[^"]*"/gi, "")
    .replace(/ on\w+='[^']*'/gi, "");

const VersionInfoButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [versions, setVersions] = useState([]);

  const fetchVersions = () => {
    setLoading(true);
    setError("");

    axiosLamApi({
      url: "/version-app",
      method: "GET",
    })
      .then((res) => {
        setVersions(res?.data?.data?.versions || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(
          getApiErrorMessage(err, "Gagal memuat informasi versi aplikasi. Periksa koneksi internet Anda.")
        );
        setLoading(false);
      });
  };

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (versions.length === 0) {
      fetchVersions();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        right: "24px",
        bottom: "24px",
        zIndex: 1080,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      {open && (
        <div
          className="card shadow-lg mb-2"
          style={{
            width: "380px",
            maxWidth: "calc(100vw - 48px)",
            border: "none",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3"
            style={{ backgroundColor: PRIMARY, color: "#fff" }}
          >
            <div className="d-flex align-items-center">
              <IconInfoCircle stroke={2} size={20} />
              &nbsp;
              <span className="fw-bolder fs-6">Informasi Versi Aplikasi</span>
            </div>
            <div className="d-flex align-items-center">
              <button
                type="button"
                title="Muat ulang"
                onClick={fetchVersions}
                className="btn btn-sm btn-icon btn-active-light-primary text-white p-0 me-2"
                style={{ height: "22px", width: "22px" }}
                disabled={loading}
              >
                <IconRefresh stroke={2} size={16} />
              </button>
              <button
                type="button"
                title="Tutup"
                onClick={() => setOpen(false)}
                className="btn btn-sm btn-icon btn-active-light-primary text-white p-0"
                style={{ height: "22px", width: "22px" }}
              >
                <IconX stroke={2} size={16} />
              </button>
            </div>
          </div>

          <div
            className="card-body p-4"
            style={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
              <span className="text-muted fs-8 text-uppercase fw-bold">
                Versi Terpasang
              </span>
              <span className="badge badge-light-primary fs-7 fw-bolder">
                {APP_VERSION}
              </span>
            </div>

            {loading && (
              <div className="text-center py-5">
                <div
                  className="spinner-border spinner-border-sm"
                  style={{ color: PRIMARY }}
                  role="status"
                />
                <div className="text-muted fs-8 mt-2">
                  Memuat informasi versi...
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="alert alert-danger d-flex flex-column py-3 mb-0">
                <span className="fs-8">{error}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-light-danger mt-2 align-self-start"
                  onClick={fetchVersions}
                >
                  Coba Lagi
                </button>
              </div>
            )}

            {!loading && !error && versions.length === 0 && (
              <div className="text-center text-muted fs-8 py-5">
                Belum ada data versi aplikasi.
              </div>
            )}

            {!loading &&
              !error &&
              versions.map((item, index) => (
                <div
                  key={item.id ?? index}
                  className={index === versions.length - 1 ? "" : "mb-5"}
                >
                  <div className="d-flex align-items-center flex-wrap mb-2">
                    <span
                      className="badge fs-8 fw-bolder me-2"
                      style={{ backgroundColor: PRIMARY, color: "#fff" }}
                    >
                      <IconTag stroke={2} size={12} />
                      &nbsp;v{item.version}
                    </span>
                    {index === 0 && (
                      <span className="badge badge-light-success fs-8 fw-bold me-2">
                        Terbaru
                      </span>
                    )}
                    <span className="text-muted fs-8 d-flex align-items-center">
                      <IconCalendar stroke={2} size={13} />
                      &nbsp;{formatReleasedAt(item.released_at)}
                    </span>
                  </div>

                  <div
                    className="fs-8 text-gray-700 version-info-description"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(item.description),
                    }}
                  />

                  {item.link && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-light-primary mt-2 py-1 px-3 fs-8"
                    >
                      <IconExternalLink stroke={2} size={14} />
                      &nbsp;Selengkapnya
                    </a>
                  )}

                  {index !== versions.length - 1 && (
                    <div className="separator separator-dashed mt-4" />
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleToggle}
        className="btn btn-sm shadow-sm d-flex align-items-center"
        style={{
          backgroundColor: open ? "#fff" : PRIMARY,
          color: open ? PRIMARY : "#fff",
          border: `1px solid ${PRIMARY}`,
          borderRadius: "20px",
          padding: "6px 12px",
          fontSize: "11px",
          fontWeight: 600,
        }}
        title="Lihat informasi versi & catatan pembaruan aplikasi"
      >
        {open ? (
          <IconX stroke={2} size={15} />
        ) : (
          <IconInfoCircle stroke={2} size={15} />
        )}
        &nbsp;{open ? "Tutup Info Versi" : "Info Versi Aplikasi"}
      </button>

      <style>{`
        .version-info-description p { margin-bottom: 0.35rem; }
        .version-info-description p:last-child { margin-bottom: 0; }
        .version-info-description ul,
        .version-info-description ol { padding-left: 1.1rem; margin-bottom: 0.35rem; }
        .version-info-description img { max-width: 100%; height: auto; }
      `}</style>
    </div>
  );
};

export default VersionInfoButton;
